import type { MensagemChat } from '@mymaxx/shared';
import { Ear, Minus, Send, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ErroApi } from '../lib/api';
import { useSessao } from '../lib/sessao';
import { sussurroApi } from '../lib/supervisao-api';
import { useSussurros, type ConversaSussurro } from '../lib/sussurros';
import { useSussurrosRecebidos } from '../lib/tempo-real';

/**
 * As janelinhas do canal paralelo, ancoradas no canto inferior direito — fora do fluxo
 * das telas, como um chat interno. Ficam abertas enquanto a pessoa navega pelo painel:
 * o supervisor fala do monitor, o atendente responde de dentro do atendimento.
 *
 * O cliente nunca vê nada disto; o servidor guarda tudo (auditável).
 */
export function PainelSussurro() {
  const { conversas, abrir, fechar, minimizar, carregar, receber } = useSussurros();

  useSussurrosRecebidos((evento) => {
    receber(evento.conversaId, evento.protocolo, evento.mensagem);
  });

  // Aviso no título da aba enquanto houver recado não lido (o painel pode estar atrás).
  const naoLidas = conversas.reduce((total, c) => total + c.naoLidas, 0);
  useEffect(() => {
    const original = document.title;
    if (naoLidas > 0) document.title = `(${naoLidas}) ${original}`;
    return () => {
      document.title = original;
    };
  }, [naoLidas]);

  if (conversas.length === 0) return null;

  return (
    <CamadaDeCima>
      <div className="pointer-events-none fixed right-4 bottom-0 z-40 flex items-end gap-2">
        {conversas.map((c) =>
          c.aberta ? (
            <Janela
              key={c.conversaId}
              conversa={c}
              onCarregar={carregar}
              onEnviar={receber}
              onMinimizar={() => minimizar(c.conversaId)}
              onFechar={() => fechar(c.conversaId)}
            />
          ) : (
            <button
              key={c.conversaId}
              type="button"
              onClick={() => abrir(c.conversaId, c.protocolo)}
              className={`pointer-events-auto mb-3 flex items-center gap-2 rounded-t-lg border px-3 py-2 text-xs shadow-lg transition ${
                c.naoLidas > 0
                  ? 'animate-pulse border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-100'
                  : 'border-neutral-300 bg-white text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200'
              }`}
            >
              <Ear size={14} />
              <span className="max-w-32 truncate">{c.protocolo}</span>
              {c.naoLidas > 0 && (
                <span className="rounded-full bg-amber-600 px-1.5 text-[10px] font-medium text-white">
                  {c.naoLidas}
                </span>
              )}
            </button>
          ),
        )}
      </div>
    </CamadaDeCima>
  );
}

/**
 * Põe as janelas de sussurro na camada de cima do navegador, acima de qualquer modal.
 *
 * Sem isto elas ficavam atrás: um `<dialog>` aberto com `showModal()` — o do Monitor, por
 * exemplo — sobe para a *top layer* e torna inerte tudo que ficou embaixo. A janela do
 * sussurro aparecia na tela, mas não aceitava um clique; quem tentava responder acabava
 * clicando no fundo do modal e fechava o atendimento que estava lendo (relato de 29/09).
 *
 * `show()` (e não `showModal()`) coloca este `<dialog>` na mesma camada **sem** inertizar o
 * resto: o supervisor lê a conversa e responde o atendente ao mesmo tempo. O elemento não
 * captura cliques fora das janelas — é `pointer-events-none`, e só as janelas reativam.
 */
function CamadaDeCima({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogo = ref.current;
    if (!dialogo || dialogo.open) return;
    dialogo.show();
    return () => dialogo.close();
  }, []);

  return (
    <dialog
      ref={ref}
      // Sem moldura nem fundo: quem desenha é o conteúdo. `inset-0` porque um dialog
      // aberto vem com posicionamento e margens próprios do agente de usuário.
      className="pointer-events-none fixed inset-0 m-0 size-full max-h-none max-w-none border-0 bg-transparent p-0 outline-none"
    >
      {children}
    </dialog>
  );
}

function Janela({
  conversa,
  onCarregar,
  onEnviar,
  onMinimizar,
  onFechar,
}: {
  conversa: ConversaSussurro;
  onCarregar: (conversaId: string, mensagens: MensagemChat[]) => void;
  onEnviar: (
    conversaId: string,
    protocolo: string,
    mensagem: MensagemChat,
    opcoes?: { minha?: boolean },
  ) => void;
  onMinimizar: () => void;
  onFechar: () => void;
}) {
  const meuId = useSessao((e) => e.usuario?.id);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const fim = useRef<HTMLDivElement>(null);
  const carregado = useRef(false);

  // Uma vez por janela: o histórico do canal (quem entra depois lê o que já foi dito).
  useEffect(() => {
    if (carregado.current) return;
    carregado.current = true;
    sussurroApi
      .historico(conversa.conversaId)
      .then((mensagens) => onCarregar(conversa.conversaId, mensagens))
      .catch(() => setErro('Não consegui carregar o histórico.'));
  }, [conversa.conversaId, onCarregar]);

  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
  }, [conversa.mensagens.length]);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const mensagem = await sussurroApi.enviar(conversa.conversaId, conteudo);
      onEnviar(conversa.conversaId, conversa.protocolo, mensagem, { minha: true });
      setTexto('');
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section className="pointer-events-auto flex h-96 w-80 flex-col rounded-t-xl border border-amber-300 bg-white shadow-2xl dark:border-amber-800 dark:bg-neutral-900">
      <header className="flex items-center gap-2 rounded-t-xl bg-amber-100 px-3 py-2 dark:bg-amber-950">
        <Ear size={15} className="shrink-0 text-amber-700 dark:text-amber-300" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-amber-900 dark:text-amber-100">Sussurro</p>
          <p className="truncate text-[11px] text-amber-700 dark:text-amber-400">
            {conversa.protocolo}
          </p>
        </div>
        <button
          type="button"
          onClick={onMinimizar}
          aria-label="Minimizar sussurro"
          className="text-amber-700 hover:text-amber-900 dark:text-amber-300"
        >
          <Minus size={16} />
        </button>
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar sussurro"
          className="text-amber-700 hover:text-amber-900 dark:text-amber-300"
        >
          <X size={16} />
        </button>
      </header>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-2">
        <p className="rounded bg-neutral-100 px-2 py-1.5 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
          Conversa da equipe sobre este atendimento — chega em quem está atendendo e na supervisão
          do setor. O cliente não vê nada daqui.
        </p>
        {conversa.mensagens.map((m) => (
          <Bolha key={m.id} mensagem={m} minha={!!meuId && m.autorId === meuId} />
        ))}
        <div ref={fim} />
      </div>

      {erro && (
        <p className="px-3 pb-1 text-[11px] text-red-600 dark:text-red-400" role="alert">
          {erro}
        </p>
      )}

      <form
        onSubmit={enviar}
        className="flex gap-1 border-t border-neutral-200 p-2 dark:border-neutral-800"
      >
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Falar com a equipe…"
          aria-label="Mensagem para a equipe"
          className="min-w-0 flex-1 rounded border border-neutral-300 px-2 py-1.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
        />
        <button
          type="submit"
          disabled={!texto.trim() || enviando}
          aria-label="Enviar sussurro"
          className="rounded bg-amber-600 px-2.5 text-white disabled:opacity-40"
        >
          <Send size={14} />
        </button>
      </form>
    </section>
  );
}

function Bolha({ mensagem, minha }: { mensagem: MensagemChat; minha: boolean }) {
  return (
    <div className={minha ? 'text-right' : ''}>
      <p className="text-[10px] text-neutral-500">{mensagem.autorNome}</p>
      <p
        className={`inline-block max-w-[85%] rounded-lg px-2 py-1 text-left text-xs ${
          minha
            ? 'bg-amber-600 text-white'
            : 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-100'
        }`}
      >
        {mensagem.texto}
      </p>
    </div>
  );
}
